import React from "react";
import MaskReveal from "../../shared/components/UIElements/MaskReveal";
import Reveal from "../../shared/components/UIElements/Reveal";
import ManagementDemo from "../components/ManagementDemo";
import FeatureIcon from "../components/FeatureIcon";
import "./styling/ProjectManagementSection.css";

const HIGHLIGHTS = [
  {
    icon: "tree",
    title: "Nested Task Tree",
    body:
      "Break a programme down as deeply as it needs to go. Drag a task to reorder it, " +
      "or drop it onto another to nest it as a subtask. Titles, descriptions, dates and " +
      "assignees are all edited in place.",
  },
  {
    icon: "timeline",
    title: "Gantt Timeline",
    body:
      "Switch the same task tree to a timeline and drag bars to reschedule. Zoom from " +
      "day to quarter, roll parent tasks up from their children, and highlight the " +
      "critical path driving your end date.",
  },
  {
    icon: "board",
    title: "Kanban Board",
    body:
      "See the whole programme as status columns and drag cards between them to " +
      "update progress — subtasks included, each traced back to its parent task. " +
      "Adding a task from a column files it under that status automatically.",
  },
  {
    icon: "link",
    title: "Dependencies & Milestones",
    body:
      "Link tasks so the schedule reflects real sequencing, and flag zero-duration " +
      "milestones for the dates that matter. Connectors turn red the moment a successor " +
      "is scheduled to start before its predecessor finishes.",
  },
  {
    icon: "people",
    title: "Assign Across Companies",
    body:
      "Assign work to project members — or to engineers at partner companies who " +
      "aren't on the project yet. Picking them adds them with a sensible starting " +
      "permission set in the same action.",
  },
];

/**
 * ProjectManagementSection — homepage section for the Management page.
 *
 * Leads with the interactive Gantt preview because the timeline is the view
 * that distinguishes this feature; the highlight cards below cover the parts a
 * static preview can't show (inline editing, drag-to-nest, the Kanban board,
 * cross-company assignment).
 */
export default function ProjectManagementSection() {
  return (
    <section className="mgmt-section" aria-labelledby="management-heading">
      <MaskReveal as="h2" id="management-heading" className="section-heading">
        Run the Project, Not Just the Model
      </MaskReveal>

      <Reveal as="p" className="mgmt-section__intro">
        Simulation work sits inside a wider engineering programme — test campaigns,
        calibration cycles, review gates, and reporting deadlines. Orecasting keeps
        that schedule in the same place as the models it governs, so the plan and
        the technical work never drift apart.
      </Reveal>

      <Reveal className="mgmt-section__demo" delay={80}>
        <ManagementDemo />
      </Reveal>

      <div className="mgmt-section__grid">
        {HIGHLIGHTS.map((h, i) => (
          <Reveal key={h.title} className="mgmt-section__card" delay={i * 70}>
            <span className="mgmt-section__card-icon" aria-hidden="true">
              <FeatureIcon name={h.icon} />
            </span>
            <h3>{h.title}</h3>
            <p>{h.body}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
